import type { MatchClient, MatchOutcome } from "./match-client";
import type { Dict, Locale } from "./i18n";
import { archetypeName, archetypeTagline, highlightText, personaName, traitLabel } from "./i18n";
import { RadarChart } from "./RadarChart";
import { formatClock, slotTheme } from "./theme";

/** Keep the key-moments strip short so the modal stays one viewport tall. */
const MAX_MOMENTS = 3;

/**
 * End-of-match settlement.
 *
 * Rendered as a fixed viewport modal (not clipped inside the arena canvas) so
 * both cricket ability cards — bars + radar — stay readable side by side.
 * Layout is deliberately compact: everything should fit without scrolling on a
 * typical laptop viewport.
 */
export function ResultOverlay({
  outcome,
  client,
  locale,
  d,
  onRematch,
  onNewDuel,
  onViewDetails,
}: {
  outcome: MatchOutcome;
  client: MatchClient;
  locale: Locale;
  d: Dict;
  /** Same fighters / AI config, fresh seed. */
  onRematch: () => void;
  /** Open the pick-both-sides duel modal. */
  onNewDuel: () => void;
  /** Close the modal and reveal the battle-timeline details. */
  onViewDetails: () => void;
}) {
  const winnerSlot = outcome.winner === "draw" ? -1 : client.slotOf(outcome.winner);
  const winnerName =
    winnerSlot < 0 ? d.draw : personaName(client.personaOf(outcome.winner), locale);

  const scoreOf = (id: string) => outcome.scores.find((s) => s.id === id)?.totalScore ?? 0;
  const crystalOf = (id: string) => outcome.scores.find((s) => s.id === id)?.score ?? 0;
  const hpOf = (id: string) => outcome.scores.find((s) => s.id === id)?.hp ?? 0;

  const reasonLabel =
    outcome.winnerReason === "score"
      ? d.reasonScore
      : outcome.winnerReason === "elimination"
        ? d.reasonElimination
        : outcome.winnerReason === "draw"
          ? d.reasonDraw
          : d.reasonTimeout;

  const moments = outcome.highlights.slice(0, MAX_MOMENTS);

  return (
    <div className="result-modal-backdrop" role="dialog" aria-modal="true">
      <div className="result-modal">
        <div className="result-head">
          <div
            className="result-winner"
            style={{ color: winnerSlot < 0 ? "var(--text)" : slotTheme(winnerSlot).color }}
          >
            <span className="result-trophy">{winnerSlot < 0 ? "🤝" : "🏆"}</span>
            {winnerSlot < 0 ? d.draw : d.wins(winnerName)}
          </div>
          <div className="result-sub">
            {reasonLabel} · {d.secondsShort(Math.round(outcome.durationTicks / 10))}
          </div>
        </div>

        <div className="result-scores">
          <div className="result-score">
            <div className="n" style={{ color: slotTheme(0).color }}>
              {scoreOf(client.infos[0].id)}
            </div>
            <div className="l">{personaName(client.infos[0].persona, locale)}</div>
            <div className="l result-score-break">
              ◆ {crystalOf(client.infos[0].id)} + {d.hp} {hpOf(client.infos[0].id)}
            </div>
          </div>
          <div className="result-vs">VS</div>
          <div className="result-score">
            <div className="n" style={{ color: slotTheme(1).color }}>
              {scoreOf(client.infos[1].id)}
            </div>
            <div className="l">{personaName(client.infos[1].persona, locale)}</div>
            <div className="l result-score-break">
              ◆ {crystalOf(client.infos[1].id)} + {d.hp} {hpOf(client.infos[1].id)}
            </div>
          </div>
        </div>

        {moments.length > 0 && (
          <div className="result-moments">
            <div className="result-moments-label">{d.keyMoments}</div>
            <div className="moments">
              {moments.map((h, i) => (
                <div className="moment" key={i}>
                  <span className="t">{formatClock(h.tick)}</span>
                  <span>
                    {highlightText(h, h.agentId ? client.displayName(h.agentId) : "?", d)}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="cards">
          {outcome.personalities.map((card, i) => {
            const theme = slotTheme(i);
            const result = outcome.scores.find((s) => s.id === client.infos[i]!.id);
            const stats = client.behaviouralOf(client.infos[i]!.id);
            return (
              <div className="card" key={i} style={{ borderColor: theme.dim }}>
                <div className="card-head" style={{ color: theme.color }}>
                  <span
                    className="swatch"
                    style={{ background: theme.color, width: 8, height: 8, borderRadius: 2 }}
                  />
                  {personaName(client.infos[i]!.persona, locale)}
                  <span className="model" style={{ color: "var(--dimmer)", fontSize: 9 }}>
                    {client.infos[i]!.model}
                  </span>
                </div>
                <div className="card-arch" style={{ color: theme.color }}>
                  {archetypeName(card.archetype, locale)}
                </div>
                <div className="card-tag">
                  {archetypeTagline(card.archetype, card.tagline, locale)}
                  {result ? ` · ${d.finishedWithHp(result.hp)}` : null}
                </div>

                {/* Bars + radar share one row so the modal stays viewport-tall
                    without dropping either ability view. */}
                <div className="card-abilities">
                  <div className="card-traits">
                    {card.traits.map((t) => (
                      <div className="trait" key={t.key}>
                        <span className="lbl">{traitLabel(t.key, d)}</span>
                        <span className="bar">
                          <i
                            style={{
                              width: `${t.value * 100}%`,
                              background: `linear-gradient(90deg, ${theme.dim}, ${theme.color})`,
                            }}
                          />
                        </span>
                        <span className="val">{Math.round(t.value * 100)}</span>
                      </div>
                    ))}
                  </div>

                  <div className="observed-style">
                    <div className="observed-title">{d.observedStyle}</div>
                    <RadarChart
                      axes={card.traits.map((t) => ({
                        key: t.key,
                        label: traitLabel(t.key, d),
                        value: t.value,
                      }))}
                      color={theme.color}
                      dim={theme.dim}
                      size={168}
                    />
                  </div>
                </div>

                {stats && (
                  <div className="stats-grid">
                    <Stat k={d.statCollected} v={stats.collectionsCompleted} />
                    <Stat k={d.statAttacks} v={stats.attacksAttempted} />
                    <Stat k={d.statDamage} v={stats.damageDealt} />
                    <Stat k={d.statTraps} v={stats.trapsSteppedOn} />
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div className="result-actions">
          <button className="primary" onClick={onRematch}>
            {d.rematch}
          </button>
          <button className="primary" onClick={onNewDuel}>
            {d.newDuel}
          </button>
          <button className="ghost" onClick={onViewDetails}>
            {d.viewDetails}
          </button>
        </div>
      </div>
    </div>
  );
}

function Stat({ k, v }: { k: string; v: number }) {
  return (
    <div className="stat">
      <div className="v">{v}</div>
      <div className="k">{k}</div>
    </div>
  );
}
